import React from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RotateCcw, Truck, PackageCheck, IndianRupee, Check, X, Loader2, ExternalLink, Clock, MapPin } from "lucide-react";
import { api, inr, fileUrl, errorMessage } from "@/lib/api";
import { Pagination, usePaged } from "@/components/Pagination";
import { Drawer, Card, Timeline } from "@/pages/AdminOrders";

const STATUSES = ["requested", "approved", "pickup_scheduled", "in_transit", "received", "refunded", "rejected", "closed"];
const label = (s) => (s || "").replace(/_/g, " ");
const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };
const COLOR = {
  requested: "bg-[#FFF7E0] text-[#8a6d00]", approved: "bg-[#E8EEF8] text-[#1D4E89]", pickup_scheduled: "bg-[#E8EEF8] text-[#1D4E89]",
  in_transit: "bg-[#E8EEF8] text-[#1D4E89]", received: "bg-[#E8F3EE] text-[#0E4D34]", refunded: "bg-[#0E4D34] text-white",
  rejected: "bg-[#FDECEC] text-[#991B1B]", closed: "bg-neutral-200 text-neutral-700",
};
export const ReturnPill = ({ status }) => <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 whitespace-nowrap ${COLOR[status] || "bg-neutral-100"}`}>{label(status)}</span>;

export default function AdminReturns() {
  const [params, setParams] = useSearchParams();
  const { data, refetch, isLoading } = useQuery({ queryKey: ["adm-returns"], queryFn: () => api.get("/admin/returns").then(r => r.data) });
  const [st, setSt] = React.useState("");
  const [q, setQ] = React.useState("");
  const open = params.get("open");
  const list = (data || []).filter(r => (!st || r.status === st) &&
    (!q || [r.return_number, r.order_number, r.customer_name, r.customer_email].some(v => (v || "").toLowerCase().includes(q.toLowerCase()))));
  const pg = usePaged(list, 20, { resetKey: `${st}|${q}` });
  const counts = (data || []).reduce((m, r) => ({ ...m, [r.status]: (m[r.status] || 0) + 1 }), {});
  return (
    <div data-testid="admin-returns">
      <div className="flex justify-between items-end gap-3 flex-wrap mb-5">
        <div>
          <h1 className="h2-editorial">Returns</h1>
          <p className="text-sm text-neutral-500 mt-1">Approve → book reverse pickup → receive & restock → refund.</p>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Return / order no., customer" className="border border-neutral-300 p-2.5 text-sm w-full sm:w-72"/>
      </div>
      <div className="flex gap-2 flex-wrap mb-4">
        <button onClick={() => setSt("")} className={`text-xs px-3 py-1.5 border rounded-full ${!st ? "bg-[#0E4D34] text-white border-[#0E4D34]" : "bg-white"}`}>All ({(data || []).length})</button>
        {STATUSES.filter(s => counts[s]).map(s => (
          <button key={s} onClick={() => setSt(s)} className={`text-xs px-3 py-1.5 border rounded-full capitalize ${st === s ? "bg-[#0E4D34] text-white border-[#0E4D34]" : "bg-white"}`}>{label(s)} ({counts[s]})</button>
        ))}
      </div>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-neutral-50 text-xs uppercase tracking-widest"><tr>{["Return", "Order", "Customer", "Items", "Refund", "Status"].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="p-6 text-center text-neutral-500">Loading…</td></tr>}
            {!isLoading && list.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">No return requests{st ? " with this status" : " yet"}.</td></tr>}
            {pg.items.map(r => (
              <tr key={r.id} className="border-t hover:bg-neutral-50 cursor-pointer" onClick={() => setParams({ open: r.id })} data-testid={`return-row-${r.return_number}`}>
                <td className="p-3"><div className="font-mono font-semibold">{r.return_number}</div><div className="text-[11px] text-neutral-500">{fmt(r.created_at)}</div></td>
                <td className="p-3 font-mono text-xs">{r.order_number}</td>
                <td className="p-3">{r.customer_name}<div className="text-xs text-neutral-500">{r.reason}</div></td>
                <td className="p-3 text-xs">{r.items.map(i => `${i.name} ×${i.quantity}`).join(", ")}</td>
                <td className="p-3">{inr(r.refund_amount)}<div className="text-[10px] uppercase text-neutral-500">{r.resolution}</div></td>
                <td className="p-3"><ReturnPill status={r.status}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pg.props} label="returns"/>
      {open && <ReturnDrawer id={open} onClose={() => setParams({})} onChanged={refetch}/>}
    </div>
  );
}

function ReturnDrawer({ id, onClose, onChanged }) {
  const qc = useQueryClient();
  const { data: list, refetch } = useQuery({ queryKey: ["adm-returns"], queryFn: () => api.get("/admin/returns").then(r => r.data) });
  const r = (list || []).find(x => x.id === id);
  const { data: order } = useQuery({ queryKey: ["adm-order", r?.order_id], queryFn: () => api.get(`/orders/${r.order_id}`).then(x => x.data), enabled: !!r });
  const [busy, setBusy] = React.useState("");
  const [note, setNote] = React.useState("");
  const [restock, setRestock] = React.useState(true);
  const [amount, setAmount] = React.useState("");
  const [ref, setRef] = React.useState("");
  React.useEffect(() => { if (r) setAmount(String(r.refund_amount ?? "")); }, [r?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return <Drawer title="Return" onClose={onClose}><div className="text-sm text-neutral-500 p-6">Loading…</div></Drawer>;
  const run = async (k, fn, ok) => {
    setBusy(k);
    try { await fn(); toast.success(ok); refetch(); onChanged?.(); qc.invalidateQueries({ queryKey: ["adm-orders"] }); qc.invalidateQueries({ queryKey: ["adm-order", r.order_id] }); setNote(""); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); }
  };
  const Btn = ({ k, children, onClick, primary, danger, testid, disabled }) => (
    <button type="button" onClick={onClick} disabled={!!busy || disabled} data-testid={testid}
            className={`text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-40 ${primary ? "bg-[#0E4D34] text-white hover:bg-[#0A3926]" : danger ? "border border-[#991B1B] text-[#991B1B] bg-white" : "border bg-white hover:border-[#0E4D34]"}`}>
      {busy === k && <Loader2 size={12} className="animate-spin"/>}{children}
    </button>
  );
  const online = order && order.payment_method === "razorpay" && order.razorpay_payment_id;
  const sh = r.shipment || {};
  const a = (order || {}).address || {};
  const done = ["refunded", "rejected", "closed"].includes(r.status);

  return (
    <Drawer title={`Return ${r.return_number}`} subtitle={`Order ${r.order_number} · ${fmt(r.created_at)}`} onClose={onClose} testid="return-drawer">
      <div className="flex items-center gap-2 flex-wrap"><ReturnPill status={r.status}/><span className="text-xs text-neutral-600">Customer wants: <b>{r.resolution}</b></span></div>

      <Card title="Items & reason" icon={RotateCcw}>
        {r.items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
            {it.image ? <img src={fileUrl(it.image)} alt="" className="w-10 h-12 object-cover rounded"/> : null}
            <div className="flex-1 text-sm">{it.name}<div className="text-xs text-neutral-500">{inr(it.price)} × {it.quantity}</div></div>
          </div>
        ))}
        <div className="mt-3 text-sm"><b>Reason:</b> {r.reason}</div>
        {r.comments && <div className="text-sm text-neutral-700 mt-1 whitespace-pre-line">"{r.comments}"</div>}
        {r.refund_upi && <div className="text-sm mt-1"><b>Refund UPI:</b> <span className="font-mono">{r.refund_upi}</span></div>}
        <div className="text-xs text-neutral-500 mt-2">Paid by {r.payment_method === "cod" ? "Cash on Delivery" : "Razorpay (online)"} · suggested refund {inr(r.refund_amount)} (shipping not refunded)</div>
      </Card>

      {order && (
        <Card title="Pickup address" icon={MapPin}>
          <div className="text-sm">{a.full_name}, {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.pincode} · {a.phone}</div>
        </Card>
      )}

      {/* 1. approve / reject */}
      {r.status === "requested" && (
        <Card title="1 · Review the request" icon={Check}>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Note to save with the decision (optional)" className="w-full border p-2 text-sm mb-2"/>
          <div className="flex gap-2">
            <Btn k="approve" primary testid="return-approve" onClick={() => run("approve", () => api.patch(`/admin/returns/${r.id}`, { status: "approved", admin_note: note }), "Return approved")}><Check size={12}/>Approve</Btn>
            <Btn k="reject" danger testid="return-reject" onClick={() => run("reject", () => api.patch(`/admin/returns/${r.id}`, { status: "rejected", admin_note: note || "Rejected" }), "Return rejected")}><X size={12}/>Reject</Btn>
          </div>
        </Card>
      )}

      {/* 2. reverse pickup */}
      {["approved", "pickup_scheduled", "in_transit"].includes(r.status) && (
        <Card title="2 · Get the parcel back" icon={Truck}>
          {sh.shipment_id ? (
            <div className="text-sm space-y-1">
              <div>Reverse pickup booked in Shiprocket {sh.courier_name ? `with ${sh.courier_name}` : ""}.</div>
              {sh.awb ? <div>AWB: <span className="font-mono">{sh.awb}</span> · <a className="text-[#0E4D34] underline inline-flex items-center gap-1" href={sh.tracking_url} target="_blank" rel="noreferrer">track<ExternalLink size={11}/></a></div>
                : <div className="text-xs text-[#8a6d00]">Courier not assigned yet{sh.awb_error ? ` (${sh.awb_error})` : ""} — assign it in Shiprocket → Returns.</div>}
              {sh.sr_status && <div className="text-xs text-neutral-500">Status: {sh.sr_status}</div>}
              {(sh.events || []).length > 0 && <Timeline events={sh.events}/>}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-neutral-600">Book a courier to collect the parcel from the customer and bring it to the store (uses the store address in Settings → Shipping). Or ask the customer to send it themselves and skip this step.</p>
              <Btn k="pickup" primary testid="return-pickup" onClick={() => run("pickup", () => api.post(`/admin/returns/${r.id}/pickup`, {}), "Reverse pickup booked")}><Truck size={12}/>Book reverse pickup (Shiprocket)</Btn>
            </div>
          )}
        </Card>
      )}

      {/* 3. receive */}
      {["approved", "pickup_scheduled", "in_transit"].includes(r.status) && (
        <Card title="3 · Parcel received at the store" icon={PackageCheck}>
          <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={restock} onChange={e => setRestock(e.target.checked)} className="accent-[#0E4D34]"/>Put the piece(s) back in stock (untick if damaged)</label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Condition note (optional)" className="w-full border p-2 text-sm mb-2"/>
          <Btn k="receive" primary testid="return-receive" onClick={() => run("receive", () => api.post(`/admin/returns/${r.id}/receive`, { restock, note }), restock ? "Received — stock updated" : "Received")}><PackageCheck size={12}/>Mark received</Btn>
        </Card>
      )}

      {/* 4. refund */}
      {["received", "approved", "pickup_scheduled", "in_transit"].includes(r.status) && r.resolution === "refund" && (
        <Card title="4 · Refund" icon={IndianRupee}>
          <div className="grid sm:grid-cols-2 gap-2 mb-2">
            <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Amount (₹)</label><input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="w-full border p-2 text-sm" data-testid="refund-amount"/></div>
            {!online && <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">UPI / bank reference</label><input value={ref} onChange={e => setRef(e.target.value)} placeholder="UTR / transaction ID" className="w-full border p-2 text-sm" data-testid="refund-ref"/></div>}
          </div>
          {online ? (
            <div className="flex gap-2 flex-wrap">
              <Btn k="refund" primary testid="refund-razorpay" onClick={() => window.confirm(`Refund ₹${amount} to the customer's original payment method via Razorpay?`) && run("refund", () => api.post(`/admin/returns/${r.id}/refund`, { amount: Number(amount), method: "auto" }), "Refund sent via Razorpay")}>Refund via Razorpay</Btn>
              <details className="text-xs"><summary className="cursor-pointer text-neutral-500 py-2">Refunded another way?</summary>
                <div className="flex gap-2 mt-2"><input value={ref} onChange={e => setRef(e.target.value)} placeholder="Reference" className="border p-2 text-sm"/>
                  <Btn k="refundm" onClick={() => run("refundm", () => api.post(`/admin/returns/${r.id}/refund`, { amount: Number(amount), method: "manual", reference: ref }), "Refund recorded")}>Save</Btn></div>
              </details>
            </div>
          ) : (
            <Btn k="refund" primary testid="refund-manual" onClick={() => run("refund", () => api.post(`/admin/returns/${r.id}/refund`, { amount: Number(amount), method: "manual", reference: ref }), "Refund recorded")}>Mark refunded</Btn>
          )}
          <p className="text-[11px] text-neutral-500 mt-2">{online ? "Razorpay sends the money back to the card / UPI / bank the customer paid with (usually 5–7 working days)." : `Cash-on-delivery order: send the money to ${r.refund_upi ? `UPI ${r.refund_upi}` : "the customer"} first, then record the reference here.`}</p>
        </Card>
      )}

      {r.refund && <Card title="Refund done" icon={IndianRupee}><div className="text-sm">{inr(r.refund.amount)} · {r.refund.method} · <span className="font-mono">{r.refund.reference}</span> · {fmt(r.refund.at)}</div></Card>}

      {!done && r.status !== "requested" && (
        <div className="flex gap-2">
          {r.resolution === "exchange" && r.status === "received" && <Btn k="close" primary onClick={() => run("close", () => api.patch(`/admin/returns/${r.id}`, { status: "closed", admin_note: "Exchange sent" }), "Closed")}>Exchange sent — close</Btn>}
          <Btn k="close2" onClick={() => window.confirm("Close this return without a refund?") && run("close2", () => api.patch(`/admin/returns/${r.id}`, { status: "closed", admin_note: note || "Closed" }), "Closed")}>Close return</Btn>
        </div>
      )}

      <Card title="History" icon={Clock}>
        <ol className="space-y-2 text-sm">{[...(r.history || [])].reverse().map((h, i) => (
          <li key={i} className="flex gap-3"><span className="text-xs text-neutral-500 w-36 shrink-0">{fmt(h.at)}</span><span><b className="capitalize">{label(h.status)}</b>{h.note ? ` — ${h.note}` : ""} <span className="text-xs text-neutral-400">({h.by})</span></span></li>))}
        </ol>
        {r.admin_note && <div className="text-xs text-neutral-600 mt-3">Note: {r.admin_note}</div>}
      </Card>
    </Drawer>
  );
}
